import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';

import {
  chatParticipantToName,
  chatParticipantsByIdAtom,
  getChatParticipants,
  getMostRecentMessageInConversation,
  messagesByConversationSidAtom,
  participantIdsByConversationSidAtom,
  unreadMessageCountByConversationSidAtom,
} from '../../session';
import { ParticipantStatus } from '../../types';
import { twilioIdentityAtom } from '../../video-sdk/twilio';
import {
  ProviderDrawerState,
  drawerStateAtom,
  sessionConversationReadyAtom,
  sessionConversationSidAtom,
  waitingVisitorsAtom,
} from '../state';
import { AllClientsChatDrawer, ParticipantConversation } from './AllClientsChatDrawer';
import { useOpenConversationCallback } from './useOpenConversationCallback';

export function AllClientsChatDrawerWrapper() {
  const sessionConversationSid = useAtomValue(sessionConversationSidAtom);
  const sessionConversationReady = useAtomValue(sessionConversationReadyAtom);
  const waitingClients = useAtomValue(waitingVisitorsAtom);
  const setDrawerState = useSetAtom(drawerStateAtom);
  const messagesByConversationSid = useAtomValue(messagesByConversationSidAtom);
  const openConversation = useOpenConversationCallback();
  const participantIds = useAtomValue(participantIdsByConversationSidAtom)[sessionConversationSid ?? ''] ?? new Set();
  const chatParticipants = useAtomValue(chatParticipantsByIdAtom);
  const twilioIdentity = useAtomValue(twilioIdentityAtom);
  const chatParticipantList = getChatParticipants(participantIds, chatParticipants, twilioIdentity);
  const unreadMessageCountByConversationSid = useAtomValue(unreadMessageCountByConversationSidAtom);
  const sessionChatTitle = Array.from(chatParticipantList).map(chatParticipantToName).join(', ');

  const waitingRoomConversations = Object.values(waitingClients)
    .map((client) => {
      const conversationSid = client.waitingRoomConversationSid;
      if (conversationSid) {
        return {
          conversationSid,
          status: ParticipantStatus.WAITING,
          chatName: client.name,
          hasUnreadMessages: unreadMessageCountByConversationSid[conversationSid] > 0,
          onOpenConversation: () => {
            openConversation(ProviderDrawerState.WAITING_ROOM_CHAT, conversationSid);
          },
          lastMessage: getMostRecentMessageInConversation(messagesByConversationSid, conversationSid),
        };
      }
    })
    .filter(Boolean) as ParticipantConversation[];
  const allConversations = [
    ...(sessionConversationReady && sessionConversationSid
      ? [
          {
            conversationSid: sessionConversationSid,
            status: ParticipantStatus.IN_SESSION,
            chatName: sessionChatTitle,
            hasUnreadMessages: unreadMessageCountByConversationSid[sessionConversationSid] > 0,
            onOpenConversation: () => {
              openConversation(ProviderDrawerState.SESSION_CHAT, sessionConversationSid);
            },
            lastMessage: getMostRecentMessageInConversation(messagesByConversationSid, sessionConversationSid),
          },
        ]
      : []),
    ...waitingRoomConversations,
  ];

  useEffect(
    function resetToWaitingRoomWhenConversationsEmpty() {
      // if drawer is open and all conversation participants leave, set state to empty waiting room chat
      if (!allConversations.length) {
        setDrawerState(ProviderDrawerState.WAITING_ROOM_CHAT);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allConversations],
  );

  return <AllClientsChatDrawer activeChats={allConversations} />;
}
